import { consola } from 'consola'
import { logOrderDebugInfo } from './orderUtils'
import { retryRequest, quickRetry, reliableRetry } from './retryUtils'
import apiProfileInstance from '@entities/user'
import { apiOrder } from '@entities/order'
import type { Order, GetRetryPaymentOrdersResponse } from '@entities/order'

/**
 * Загружает информацию о заказе с быстрым retry
 * @param orderId - ID заказа
 * @returns информация о заказе или null
 */
export async function fetchOrderInfo(orderId: string): Promise<{ status: string; paymentStatus: string } | null> {
  if (!orderId) return null
  
  consola.debug('🔄 fetchOrderInfo called for order:', orderId)
  
  try {
    const statusData = await quickRetry(() => apiOrder.getOrder(orderId))
    consola.debug('📡 API response for order status:', statusData)
    return statusData
  } catch (error: any) {
    consola.warn('❌ Failed to fetch order info (non-critical):', error)
    return null
  }
}

/**
 * Загружает полную информацию о заказе с надежным retry
 * @param orderId - ID заказа
 * @returns полная информация о заказе или null
 */
export async function fetchOrderDetails(orderId: string): Promise<Order | null> {
  consola.debug('Loading order details for:', orderId)
  
  try {
    const orderDetails = await retryRequest(
      () => apiOrder.getOrderDetails(orderId),
      3, // максимум 3 попытки
      1000, // начальная задержка 1 секунда
    )
    
    consola.debug('Raw order details from API:', orderDetails)
    
    // Проверяем что данные действительно получены
    if (!orderDetails) {
      throw new Error('No order details received from API')
    }
    
    if (!orderDetails.items || orderDetails.items.length === 0) {
      consola.warn('Order details received but no items found:', orderDetails)
    }
    
    logOrderDebugInfo(orderDetails, 'API order details response')
    
    return orderDetails
  } catch (error: any) {
    consola.error('Ошибка при загрузке деталей заказа:', error)
    consola.error('Error details:', {
      message: error.message,
      status: error.status,
      response: error.response,
    })
    throw error
  }
}

/**
 * Запрашивает неоплаченные заказы с надежным retry
 * @param userId - ID пользователя
 * @returns ответ API с неоплаченными заказами
 */
export async function fetchUnpaidOrdersFromAPI(userId: string): Promise<GetRetryPaymentOrdersResponse> {
  consola.debug('=== fetchUnpaidOrdersFromAPI() called ===')
  
  try {
    const response = await reliableRetry(() => apiOrder.retryPayment(userId))
    consola.debug('Retry payment API response:', response)
    return response
  } catch (error: any) {
    consola.error('Failed to fetch unpaid orders from API:', error)
    throw error
  }
}

/**
 * Проверяет доступность API профиля
 * @returns true если API доступно
 */
export async function checkAPIAvailability(): Promise<boolean> {
  try {
    await apiProfileInstance.getUser()
    return true
  } catch {
    return false
  }
}

/**
 * Выполняет запрос с обработкой ошибок сети
 * @param requestFn - функция запроса
 * @returns результат запроса или null при ошибке
 */
export async function safeAPIRequest<T>(requestFn: () => Promise<T>): Promise<T | null> {
  try {
    return await requestFn()
  } catch (error: any) {
    if (error.name === 'NetworkError' || error.code === 'NETWORK_ERROR') {
      consola.warn('Network error detected, treating as non-critical:', error)
      return null
    }
    throw error
  }
}

/**
 * Создает функцию для повторного выполнения API запроса
 * @param apiCall - функция API вызова
 * @param operationName - название операции для логирования
 * @returns функция для выполнения с retry
 */
export function createRetryableAPICall<T>(
  apiCall: () => Promise<T>,
  operationName: string,
): () => Promise<T> {
  return async () => {
    consola.debug(`🔄 Starting ${operationName}...`)
    try {
      const result = await retryRequest(apiCall)
      consola.debug(`✅ ${operationName} completed successfully`)
      return result
    } catch (error) {
      consola.error(`❌ ${operationName} failed after all retries:`, error)
      throw error
    }
  }
}

/**
 * Логирует API запрос
 * @param endpoint - конечная точка API
 * @param params - параметры запроса
 */
export function logAPIRequest(endpoint: string, params?: any): void {
  consola.debug(`📡 API Request: ${endpoint}`, params || '')
}

/**
 * Логирует ответ API
 * @param endpoint - конечная точка API
 * @param response - ответ API
 */
export function logAPIResponse(endpoint: string, response: any): void {
  consola.debug(`📡 API Response: ${endpoint}`, response)
}

/**
 * Логирует ошибку API
 * @param endpoint - конечная точка API
 * @param error - ошибка API
 */
export function logAPIError(endpoint: string, error: any): void {
  consola.error(`📡 API Error: ${endpoint}`, {
    message: error.message,
    status: error.status,
    code: error.code,
  })
}